/**
 * Shared input shape for all per-engine data-profile SQL builders.
 */
import type { DatabaseEngineId } from '../../../../../core/engines';

export type ProfilingColumnTypeBucket =
  | 'numeric'
  | 'datetime'
  | 'string'
  | 'opaque';

export type ProfilingSqlOpts = {
  engine: DatabaseEngineId;
  schema: string;
  table: string;
  column: string;
  /** Source data type hint — drives type-aware skipping (numeric vs date vs string vs opaque). */
  dataType?: string;

  /** Histogram inputs */
  bucketCount?: number;
  /** Pre-computed min/max for equal-width histograms (avoids a sub-query). */
  min?: number;
  max?: number;
  /**
   * Optional axis trim. When set, the histogram is computed only over rows
   * with `axisMin <= col <= axisMax` AND the bucket bounds use [axisMin,
   * axisMax] instead of [min, max]. Used to "zoom in" on the dense region
   * of a heavily skewed distribution.
   */
  axisMin?: number;
  axisMax?: number;

  /** Top-N row count */
  n?: number;

  /** Addendum: optional WHERE body (no leading WHERE) and row cap. */
  whereClause?: string;
  rowLimit?: number;

  /**
   * Force the engine-specific math fallback even when the engine claims to
   * support a native window function. Used by the quartiles flow to retry
   * after a runtime failure (e.g. legacy MSSQL at compat level < 110 where
   * PERCENTILE_CONT errors out).
   */
  forceMathFallback?: boolean;
};

const NUMERIC_TYPES = new Set([
  'int', 'integer', 'smallint', 'tinyint', 'bigint',
  'float', 'real', 'double', 'double precision',
  'decimal', 'numeric', 'money', 'smallmoney',
  'number', // Oracle
  'serial', 'bigserial', 'smallserial', // Postgres serial sugar
]);

const DATE_TYPES = new Set([
  'date',
  'datetime', 'datetime2', 'datetimeoffset', 'smalldatetime',
  'timestamp', 'timestamptz',
  'timestamp with time zone',
  'timestamp without time zone',
  'time',
]);

const OPAQUE_TYPES = new Set([
  'binary', 'varbinary', 'image', 'blob',
  'bytea',
  'geometry', 'geography',
  'xml', 'json', 'jsonb',
  'uniqueidentifier', 'uuid',
]);

function basetype(dataType: string | undefined): string {
  if (!dataType) return '';
  return dataType.toLowerCase().replace(/\(.*\)/, '').trim();
}

export function classifyColumnType(dataType: string | undefined): ProfilingColumnTypeBucket {
  const base = basetype(dataType);
  if (!base) return 'string';
  if (OPAQUE_TYPES.has(base)) return 'opaque';
  if (DATE_TYPES.has(base) || base.startsWith('datetime') || base.startsWith('timestamp')) return 'datetime';
  if (NUMERIC_TYPES.has(base)) return 'numeric';
  return 'string';
}
